import { CheckOutlined, LockOutlined, ReloadOutlined } from "@ant-design/icons"
import { Alert, Button, Flex, Popconfirm, Skeleton, Typography, notification } from "antd"
import React, { useState } from "react"

import { CardComponent } from "../../../../components/Card/CardComponent.tsx"
import { RequirementRow } from "./RequirementRow.tsx"
import type { LeadLifecycleRequirement } from "./lead-lifecycle.types.ts"
import { useLeadLifecycleRules } from "./useLeadLifecycleRules.ts"

const { Title, Text } = Typography

const SECTION_ORDER = ["customer", "meeting_ownership", "scope_commercials"] as const

const SECTION_TITLES: Record<string, string> = {
  customer: "Customer",
  meeting_ownership: "Meeting & Ownership",
  scope_commercials: "Scope & Commercials"
}

const groupBySection = (requirements: LeadLifecycleRequirement[]) => {
  const groups = new Map<string, LeadLifecycleRequirement[]>()
  for (const requirement of requirements) {
    const list = groups.get(requirement.section) ?? []
    list.push(requirement)
    groups.set(requirement.section, list)
  }
  return groups
}

export function LeadLifecycleSettings() {
  const {
    requirements,
    drafts,
    loading,
    loadFailed,
    saving,
    saveError,
    dirty,
    reload,
    setStage,
    setFields,
    save,
    resetToDefaults
  } = useLeadLifecycleRules()

  const [expandedKey, setExpandedKey] = useState<string | null>(null)

  const onSave = async () => {
    const ok = await save()
    if (ok) {
      notification.success({ message: "Lead Life Cycle settings updated." })
    }
  }

  const onReset = async () => {
    const ok = await resetToDefaults()
    if (ok) {
      notification.success({ message: "Lead Life Cycle settings reset to defaults." })
    }
  }

  if (loading) {
    return (
      <CardComponent>
        <Skeleton active paragraph={{ rows: 8 }} />
      </CardComponent>
    )
  }

  if (loadFailed) {
    return (
      <CardComponent>
        <Alert
          action={
            <Button icon={<ReloadOutlined />} onClick={() => void reload()} size="small">
              Retry
            </Button>
          }
          description="Something went wrong loading these settings."
          showIcon
          type="error"
        />
      </CardComponent>
    )
  }

  const groups = groupBySection(requirements)

  return (
    <div className="flex flex-col gap-4">
      <Flex align="flex-start" justify="space-between">
        <div>
          <Title className="m-0" level={2}>
            Lead Life Cycle
          </Title>
          <Text type="secondary">
            Choose the earliest stage at which each requirement applies. A later stage
            always keeps every earlier stage&apos;s requirements.
          </Text>
        </div>
        <Flex gap="small">
          <Popconfirm
            cancelText="Cancel"
            okText="Reset"
            onConfirm={() => void onReset()}
            title="Reset to defaults?"
            description="This clears every custom rule for your team and returns each requirement to EZBuild's default stage."
          >
            <Button disabled={saving}>Reset to defaults</Button>
          </Popconfirm>
          <Button
            disabled={!dirty}
            icon={<CheckOutlined />}
            loading={saving}
            onClick={() => void onSave()}
            type="primary"
          >
            Save changes
          </Button>
        </Flex>
      </Flex>

      {saveError && <Alert description={saveError} showIcon type="error" />}

      <CardComponent>
        <Flex className="mb-3 flex-wrap gap-4 border-b border-gray-100 pb-3" gap="middle">
          <Flex align="center" gap={4}>
            <span className="inline-block h-3 w-3 rounded-full bg-blue-600" />
            <Text className="text-xs" type="secondary">
              Required from this stage
            </Text>
          </Flex>
          <Flex align="center" gap={4}>
            <span className="inline-block h-3 w-3 rounded-full border border-blue-200 bg-blue-50" />
            <Text className="text-xs" type="secondary">
              Inherited from an earlier stage
            </Text>
          </Flex>
          <Flex align="center" gap={4}>
            <LockOutlined className="text-gray-400" />
            <Text className="text-xs" type="secondary">
              Locked — always required
            </Text>
          </Flex>
          <Flex align="center" gap={4}>
            <span className="inline-block h-3 w-3 rounded-full border border-gray-200 bg-white" />
            <Text className="text-xs" type="secondary">
              Not required
            </Text>
          </Flex>
        </Flex>

        {SECTION_ORDER.map((section) => {
          const rows = groups.get(section)
          if (!rows?.length) return null
          return (
            <div className="mb-4 last:mb-0" key={section}>
              <Text className="mb-1 block font-semibold" type="secondary">
                {SECTION_TITLES[section] ?? section}
              </Text>
              {rows.map((requirement) => {
                const draft = drafts[requirement.key]
                if (!draft) return null
                return (
                  <RequirementRow
                    draft={draft}
                    expanded={expandedKey === requirement.key}
                    key={requirement.key}
                    onFieldsChange={(fields) => setFields(requirement.key, fields)}
                    onStageChange={(stage) => setStage(requirement.key, stage)}
                    onToggleExpanded={() =>
                      setExpandedKey((prev) =>
                        prev === requirement.key ? null : requirement.key
                      )
                    }
                    requirement={requirement}
                  />
                )
              })}
            </div>
          )
        })}
      </CardComponent>
    </div>
  )
}
